// Script VM (SCR-1/3, E-30). PURE (ENG-1) — a tree-walking interpreter that steps
// inside the sim tick.
//
// Determinism: the VM reads only its inputs (the program, the host's snapshot
// reads, and the per-dispatch self/event views) and has no randomness or clock, so
// the same program against the same host produces the same effects every time —
// which is what lets scripts replay under REP.
//
// Safety (E-30, "abort not throw", cf. E-23): every path that could run away — the
// op budget, the nesting depth, a bad type, a fill over SCR_FILL_MAX — aborts *this
// dispatch only* by raising a ScriptError that `run()` catches and returns as a
// failed result. Nothing ever throws into the tick loop.
//
// User functions (SCR-5, ARCH §27.2): a call binds its arguments in a fresh frame
// holding ONLY the params, and that frame spends one unit of the SAME `SCR.MAX_DEPTH`
// a `{}` block spends — there is one depth limit, not two. The call graph is proven
// acyclic at compile time, so a call chain is finite and needs no stack guard.
//
// THE MOB SURFACE (§27.5/§27.6, M45.3): `RunOptions.mob` is the second, OPTIONAL half
// of the verb table — the one `self.*`/`mem.*`/`handled()` reach. It is forwarded into
// the per-dispatch `VerbCtx` alongside `setblockMax`, and that forwarding is the whole
// reason a mob hook can do anything at all: M45.4 built the verbs and `ScriptMobHost`,
// but with no field on `RunOptions` there was no way to hand `run()` a mob, so every
// one of those verbs was unreachable from a compiled program. Absent ⇒ a block
// dispatch, exactly as before: those verbs abort and the block-only verbs are allowed.
import { SCR } from '../../config';
import { ScriptError } from './lex';
import type { Program, Stmt, Expr, FnDecl } from './parse';
import {
  type Value, valueType, valuesEqual, valueToString, isVec3, vec3,
} from './values';
import { type ScriptHost, type ScriptMobHost, type VerbCtx, callVerb } from '../api';
import type { ScriptRng } from '../rng';

/** The script block's own state, exposed as `self.*`. */
export interface SelfView { x: number; y: number; z: number; powered: boolean }
/** The dispatched event, exposed as `event.*`.
 *
 *  `name` selects the `on <name>` handler and `power` is the redstone level; both are
 *  always present, so every pre-M46 source reads exactly what it always did. The three
 *  M46 fields are OPTIONAL because they are per-hook (§27.6's payload table, and
 *  §27.9's block hooks): `on hurt` has an `amount`, `on placed` does not.
 *
 *  Reading a field the firing hook does not carry is a dispatch ABORT, not a silent 0
 *  or an empty string (see `eventProp`). A script that asks `on placed` for
 *  `event.amount` has a bug, and a fabricated default would hide it until it surfaced
 *  as behaviour nobody could trace back to the read. */
export interface EventView {
  name: string;
  power: number;
  /** `on hurt` / `on death`: damage dealt (§27.6). */
  amount?: number;
  /** Who or what caused this event (§27.6) — and for §27.9's `on neighbor`, the vec3
   *  of the face-adjacent cell that changed, which is how a handler tells a change
   *  below it from one beside it. */
  source?: Value;
  /** `on signal`: the single value `emit` carried (§27.10 — one value, never a list
   *  and never a bag, D-TF-105). */
  payload?: Value;
}

export interface RunOptions {
  self: SelfView;
  event: EventView;
  opBudget?: number;
  maxDepth?: number;
  fillMax?: number;
  repeatMax?: number;
  entityRange?: number;
  /** The per-dispatch seeded RNG for `random`/`chance` (§27.8). The caller derives
   *  it per dispatch and never stores it; omitted, those two verbs abort. */
  rng?: ScriptRng;
  /** The mob this dispatch is FOR (§27.5's mob slice, §27.6's hooks) — the surface
   *  `self.*`, `mem.*` and `handled()` write through. Absent ⇒ a block dispatch:
   *  those verbs abort and `fill`/`give`/`aircraft.setControl` stay legal. */
  mob?: ScriptMobHost;
  /** `SCR.MOB.SETBLOCK_MAX` — cells ONE mob dispatch may set in total (§27.5). It
   *  rides here rather than being read inside `api.ts` so the ceiling a dispatch was
   *  charged against is visible at the call site (M45.5 lowers it for `scriptHz: 20`).
   *  Omitted, `api.ts` still charges the config ceiling: absence is not "unlimited". */
  setblockMax?: number;
  /** §27.10's per-tick spawn charger, forwarded to `VerbCtx.spawn`. It belongs to the
   *  RUNTIME, not the dispatch — the budget is shared across every script in the tick —
   *  so the VM only passes it through. Omitted ⇒ `summon` is unbudgeted, which is the
   *  pre-M46.4 behaviour. */
  spawn?: () => boolean;
  /** The `on <name>` handler to run, when it differs from `event.name`. Only `on
   *  signal` needs it: §27.6 gives that row an `event.name` of its own (the signal's
   *  name, which §5.1's `pack.tfs` compares against), while the block to execute is
   *  still `signal`. Omitted ⇒ `event.name`, the pre-M46.5 behaviour for every other
   *  hook (D-TF-217). */
  handler?: string;
}

export interface ScriptResult {
  /** false ⇒ the dispatch aborted (budget/depth/type error) — see `error`. */
  ok: boolean;
  /** VM steps consumed (statements + evaluated expression nodes + loop turns). */
  ops: number;
  error?: ScriptError;
}

type Env = Map<string, Value>[];

/** What a `fn` yields when it falls off its end without a `return` — the language has
 *  no null, so an effects-only helper reads as 0 in an expression (and as a bool it is
 *  a type error that aborts the dispatch, E-30, rather than a silent truth). */
const FN_NO_RETURN: Value = 0;

class BudgetError extends ScriptError {}
class DepthError extends ScriptError {}

class Interp {
  ops = 0;
  private depth = 0;
  /** Top-level `fn`s by name, indexed once per run. */
  private fns: ReadonlyMap<string, FnDecl> = new Map();
  /** Set by a `return`; every statement loop unwinds while it holds a value and the
   *  call frame that started the fn consumes it. `return` is a compile error outside a
   *  fn, and there is no `break`, so this is the VM's only non-linear control flow. */
  private returning: { value: Value } | null = null;
  private readonly opBudget: number;
  private readonly maxDepth: number;
  private readonly verbCtx: VerbCtx;
  private readonly repeatMax: number;
  /** The `on <name>` block this dispatch selects — `opts.handler`, or `event.name`
   *  when the caller did not separate them (which is every hook but `on signal`). */
  private readonly handler: string;

  constructor(private readonly host: ScriptHost, private readonly opts: RunOptions) {
    this.opBudget = opts.opBudget ?? SCR.OP_BUDGET;
    this.maxDepth = opts.maxDepth ?? SCR.MAX_DEPTH;
    this.repeatMax = opts.repeatMax ?? SCR.REPEAT_MAX;
    this.handler = opts.handler ?? opts.event.name;
    // ONE VerbCtx per dispatch, thrown away with the interpreter — which is what makes
    // `VerbTally` (the mob `setblock` running total) per-dispatch by construction.
    this.verbCtx = {
      host,
      fillMax: opts.fillMax ?? SCR.FILL_MAX,
      entityRange: opts.entityRange ?? SCR.ENTITY_RANGE,
      rng: opts.rng,
      self: vec3(opts.self.x, opts.self.y, opts.self.z),
      mob: opts.mob,
      setblockMax: opts.setblockMax,
      spawn: opts.spawn,
    };
  }

  private step(): void {
    if (++this.ops > this.opBudget) throw new BudgetError(`op budget exceeded (${this.opBudget})`);
  }

  run(program: Program): void {
    this.fns = new Map<string, FnDecl>(program.fns.map((f) => [f.name, f]));
    const env: Env = [new Map()];
    for (const s of program.body) this.execStmt(s, env);
  }

  private execBlock(stmts: Stmt[], parent: Env): void {
    if (++this.depth > this.maxDepth) throw new DepthError(`nesting too deep (max ${this.maxDepth})`);
    const env: Env = [...parent, new Map()];
    for (const s of stmts) { this.execStmt(s, env); if (this.returning) break; }
    this.depth--;
  }

  private execStmt(s: Stmt, env: Env): void {
    this.step();
    switch (s.kind) {
      case 'let': env[env.length - 1]!.set(s.name, this.eval(s.expr, env)); return;
      case 'assign': this.assign(env, s.name, this.eval(s.expr, env)); return;
      case 'expr': this.eval(s.expr, env); return;
      case 'if': {
        if (this.bool(this.eval(s.cond, env), s.pos)) this.execBlock(s.then, env);
        else if (s.otherwise) this.execBlock(s.otherwise, env);
        return;
      }
      case 'repeat': {
        let n = Math.floor(this.number(this.eval(s.count, env), s.pos));
        if (!Number.isFinite(n) || n < 0) n = 0;
        if (n > this.repeatMax) n = this.repeatMax;
        for (let k = 0; k < n; k++) { this.step(); this.execBlock(s.body, env); if (this.returning) break; }
        return;
      }
      case 'on': {
        // WHICH HANDLER RUNS is `handler`, not `event.name`. For every hook but one the
        // two are the same string and this is a distinction without a difference. `on
        // signal` is the exception §27.6 forces: its row carries `name`, and §5.1's
        // binding `pack.tfs` reads `event.name == "hunt"` — so for a signal, `name` is
        // the SIGNAL's name while the block to run is `signal` (D-TF-217).
        if (this.handler === s.event) this.execBlock(s.body, env);
        return;
      }
      case 'return': this.returning = { value: this.eval(s.expr, env) }; return;
    }
  }

  private assign(env: Env, name: string, val: Value): void {
    for (let d = env.length - 1; d >= 0; d--) if (env[d]!.has(name)) { env[d]!.set(name, val); return; }
    env[env.length - 1]!.set(name, val); // unreachable after validation, but total
  }

  private eval(e: Expr, env: Env): Value {
    this.step();
    switch (e.kind) {
      case 'num': return e.value;
      case 'str': return e.value;
      case 'bool': return e.value;
      case 'var': {
        for (let d = env.length - 1; d >= 0; d--) { const v = env[d]!.get(e.name); if (v !== undefined) return v; }
        throw new ScriptError(`unknown name '${e.name}'`, e.pos); // unreachable after validation
      }
      case 'unary': return this.unary(e, env);
      case 'binary': return this.binary(e, env);
      case 'member': return this.member(e, env);
      case 'call': return this.call(e, env);
    }
  }

  private unary(e: Expr & { kind: 'unary' }, env: Env): Value {
    const v = this.eval(e.operand, env);
    if (e.op === '-') return -this.number(v, e.pos);
    return !this.bool(v, e.pos);
  }

  private binary(e: Expr & { kind: 'binary' }, env: Env): Value {
    // Short-circuit logical operators.
    if (e.op === '&&') { const l = this.bool(this.eval(e.left, env), e.pos); return l ? this.bool(this.eval(e.right, env), e.pos) : false; }
    if (e.op === '||') { const l = this.bool(this.eval(e.left, env), e.pos); return l ? true : this.bool(this.eval(e.right, env), e.pos); }
    const l = this.eval(e.left, env);
    const r = this.eval(e.right, env);
    switch (e.op) {
      case '==': return valuesEqual(l, r);
      case '!=': return !valuesEqual(l, r);
      case '+':
        if (typeof l === 'string' || typeof r === 'string') return valueToString(l) + valueToString(r);
        return this.number(l, e.pos) + this.number(r, e.pos);
      case '-': return this.number(l, e.pos) - this.number(r, e.pos);
      case '*': return this.number(l, e.pos) * this.number(r, e.pos);
      case '/': return this.number(l, e.pos) / this.number(r, e.pos);
      case '%': return this.number(l, e.pos) % this.number(r, e.pos);
      case '<': return this.number(l, e.pos) < this.number(r, e.pos);
      case '<=': return this.number(l, e.pos) <= this.number(r, e.pos);
      case '>': return this.number(l, e.pos) > this.number(r, e.pos);
      case '>=': return this.number(l, e.pos) >= this.number(r, e.pos);
      default: throw new ScriptError(`unknown operator '${e.op}'`, e.pos);
    }
  }

  private member(e: Expr & { kind: 'member' }, env: Env): Value {
    const base = e.obj;
    if (base.kind === 'var') {
      // A real (vec3) variable shadows the self/event namespaces (matches validation).
      let bound = false;
      for (let d = env.length - 1; d >= 0 && !bound; d--) bound = env[d]!.has(base.name);
      if (!bound) {
        if (base.name === 'self') return this.selfProp(e.prop, e);
        if (base.name === 'event') return this.eventProp(e.prop, e);
      }
    }
    return this.vecProp(this.eval(base, env), e.prop, e);
  }

  private selfProp(prop: string, e: Expr): Value {
    const s = this.opts.self;
    if (prop === 'x') return s.x;
    if (prop === 'y') return s.y;
    if (prop === 'z') return s.z;
    if (prop === 'powered') return s.powered;
    // §27.2's `self.pos`: the same three numbers as a vec3, so a script can pass its
    // own position to `distance`/`faceAt` without rebuilding it component-wise.
    if (prop === 'pos') return vec3(s.x, s.y, s.z);
    throw new ScriptError(`'self' has no property '${prop}'`, e.pos);
  }

  private eventProp(prop: string, e: Expr): Value {
    const ev = this.opts.event;
    if (prop === 'name') return ev.name;
    if (prop === 'power') return ev.power;
    // The per-hook fields (§27.6/§27.9). Present ⇒ the value; absent ⇒ an abort naming
    // the hook, because the alternative — a fabricated 0 — turns "you read a field this
    // hook does not have" into behaviour the author cannot trace back to the read.
    if (prop === 'amount' || prop === 'source' || prop === 'payload') {
      const v = ev[prop];
      if (v === undefined) {
        throw new ScriptError(`'event.${prop}' is not carried by 'on ${ev.name}'`, e.pos);
      }
      return v;
    }
    throw new ScriptError(`'event' has no property '${prop}'`, e.pos);
  }

  private vecProp(v: Value, prop: string, e: Expr): Value {
    if (isVec3(v) && (prop === 'x' || prop === 'y' || prop === 'z')) return v[prop];
    throw new ScriptError(`cannot read '.${prop}' of a ${valueType(v)}`, e.pos);
  }

  private call(e: Expr & { kind: 'call' }, env: Env): Value {
    const callee = e.callee;
    const name = callee.kind === 'var'
      ? callee.name
      : (callee.kind === 'member' && callee.obj.kind === 'var')
        ? `${callee.obj.name}.${callee.prop}`
        : (() => { throw new ScriptError('expression is not callable', e.pos); })();
    const args = e.args.map((a) => this.eval(a, env));
    // A user `fn` resolves first; parse.ts refuses a fn named after a verb, so this
    // can never shadow the verb table (§27.2). Everything else is a whitelisted verb.
    const fn = this.fns.get(name);
    return fn ? this.callFn(fn, args) : callVerb(name, args, this.verbCtx);
  }

  /** Bind a user fn's arguments in a fresh call frame and run its body (SCR-5). The
   *  frame costs one unit of `maxDepth` — the same budget a `{}` block spends (E-30) —
   *  and the body sees only its params, matching what the parser validated. */
  private callFn(fn: FnDecl, args: Value[]): Value {
    if (++this.depth > this.maxDepth) throw new DepthError(`nesting too deep (max ${this.maxDepth})`);
    const frame: Env = [new Map()];
    for (let k = 0; k < fn.params.length; k++) frame[0]!.set(fn.params[k]!, args[k]!); // arity is compile-checked
    for (const s of fn.body) { this.execStmt(s, frame); if (this.returning) break; }
    this.depth--;
    const ret = this.returning;
    this.returning = null;
    return ret ? ret.value : FN_NO_RETURN;
  }

  private number(v: Value, pos: Expr['pos']): number {
    if (typeof v !== 'number') throw new ScriptError(`expected a number, got ${valueType(v)}`, pos);
    return v;
  }
  private bool(v: Value, pos: Expr['pos']): boolean {
    if (typeof v !== 'boolean') throw new ScriptError(`expected a bool, got ${valueType(v)}`, pos);
    return v;
  }
}

/** Run a compiled program against a host for one dispatch. Deterministic; never
 *  throws — a budget/depth/type failure comes back as `{ ok: false, error }`. */
export function run(program: Program, host: ScriptHost, opts: RunOptions): ScriptResult {
  const interp = new Interp(host, opts);
  try {
    interp.run(program);
    return { ok: true, ops: interp.ops };
  } catch (e) {
    const error = e instanceof ScriptError ? e : new ScriptError(`runtime error: ${(e as Error).message}`);
    return { ok: false, ops: interp.ops, error };
  }
}

export { vec3 };
